import { useEffect, useMemo, useState } from 'react'
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts'
import type { DatedPoint, GraphApiResponse, GraphPeriod, GraphPoint, GraphWidgetOptions } from './types'
import { CorrelationMatrixView } from './CorrelationMatrixView'
import { RatesSummaryView } from './RatesSummaryView'

type StrengthRow = { x: number | string } & Record<string, number | string>

type GraphSeries =
    | { kind: 'pair'; points: GraphPoint[] }
    | { kind: 'strength'; rows: StrengthRow[]; keys: string[] }

export type GraphWidgetViewState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'success'; data: GraphApiResponse | null }

export type GraphWidgetViewProps = {
    graphId: string
    title?: string
    height?: number
    theme?: GraphWidgetOptions['theme']
    period?: GraphWidgetOptions['period']
    state: GraphWidgetViewState
}

function toFiniteNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toDateString(value: unknown): string | null {
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    if (!trimmed) return null
    return trimmed
}

function normalizeResponse(data: GraphApiResponse, graphId: string): GraphSeries | null {
    if (!data || typeof data !== 'object') return null

    const pairValues = data.pairs?.[graphId]
    if (Array.isArray(pairValues)) {
        // Backward compatible: number[] or { y, t?, x? }[]
        if (pairValues.length > 0 && typeof pairValues[0] === 'object' && pairValues[0] !== null) {
            return {
                kind: 'pair',
                points: (pairValues as DatedPoint[])
                    .map((value, index) => {
                        const y = toFiniteNumber((value as { y?: unknown }).y)
                        if (y === null) return null
                        const t = toDateString((value as { t?: unknown }).t) ?? undefined
                        const xRaw = (value as { x?: unknown }).x
                        const x = (typeof xRaw === 'string' || typeof xRaw === 'number')
                            ? xRaw
                            : (t ?? index)
                        return { x, y, t } as GraphPoint
                    })
                    .filter((point): point is GraphPoint => Boolean(point)),
            }
        }

        return {
            kind: 'pair',
            points: pairValues
                .map((value, index) => {
                    const y = toFiniteNumber(value)
                    return y === null ? null : ({ x: index, y } as GraphPoint)
                })
                .filter((point): point is GraphPoint => Boolean(point)),
        }
    }

    if (Array.isArray(data.series)) {
        const strengthKeys = new Set<string>()
        data.series.forEach((entry) => {
            if (!entry || typeof entry !== 'object') return
            const maybe = entry as { strength?: Record<string, unknown> }
            if (!maybe.strength) return
            Object.keys(maybe.strength).forEach((key) => strengthKeys.add(key))
        })

        const keys = Array.from(strengthKeys)
        const wantsAllStrength = graphId === 'strength' || graphId === 'strengths' || graphId === 'all'

        if (wantsAllStrength && keys.length > 0) {
            const rows: StrengthRow[] = data.series.map((entry, index) => {
                const maybe = entry as { i?: unknown; t?: unknown; strength?: Record<string, unknown> }
                const t = toDateString(maybe?.t)
                const x = t ?? toFiniteNumber(maybe?.i) ?? index
                const row: StrengthRow = { x }
                keys.forEach((key) => {
                    const value = maybe?.strength ? toFiniteNumber(maybe.strength[key]) : null
                    if (value !== null) row[key] = value
                })
                return row
            })
            return { kind: 'strength', rows, keys }
        }

        const points: GraphPoint[] = []
        data.series.forEach((entry, index) => {
            if (!entry || typeof entry !== 'object') return
            const maybe = entry as { i?: unknown; t?: unknown; strength?: Record<string, unknown> }
            const y = maybe.strength ? toFiniteNumber(maybe.strength[graphId]) : null
            if (y === null) return
            const t = toDateString(maybe.t) ?? undefined
            const x = t ?? toFiniteNumber(maybe.i) ?? index
            points.push({ x, y, t })
        })
        return { kind: 'pair', points }
    }

    return null
}

function formatNumber(value: number, fractionDigits = 2): string {
    if (!Number.isFinite(value)) return String(value)
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value)
}

function formatPercent(value: number): string {
    if (!Number.isFinite(value)) return String(value)
    const sign = value > 0 ? '+' : value < 0 ? '' : ''
    return `${sign}${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`
}

function formatMonthDay(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}`
}

function formatHourMinute(date: Date): string {
    return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** Format a date based on the active period. Day period includes time; others show M/d. */
function formatDateForPeriod(date: Date, period?: GraphPeriod): string {
    if (period === 'day') {
        return `${formatMonthDay(date)} ${formatHourMinute(date)}`
    }
    return formatMonthDay(date)
}

/** Try to parse a `t` field (ISO date/datetime) from chart data rows. */
function parseDateFromT(rows: Record<string, unknown>[], index: number): Date | null {
    const row = rows[index]
    if (!row) return null
    const t = row.t ?? row.x
    if (typeof t !== 'string') return null
    const d = new Date(t)
    return Number.isNaN(d.getTime()) ? null : d
}

function getPeriodFallbackStartDate(period?: GraphPeriod): Date {
    const now = new Date()
    switch (period) {
        case 'day': {
            const d = new Date(now)
            d.setHours(0, 0, 0, 0)
            return d
        }
        case 'week': {
            const day = now.getDay()
            const diffFromMonday = (day + 6) % 7
            const monday = new Date(now)
            monday.setDate(now.getDate() - diffFromMonday)
            monday.setHours(0, 0, 0, 0)
            return monday
        }
        case 'this_month': {
            return new Date(now.getFullYear(), now.getMonth(), 1)
        }
        case 'month': {
            const d = new Date(now)
            d.setDate(d.getDate() - 30)
            d.setHours(0, 0, 0, 0)
            return d
        }
        default: {
            // fallback: this week's Monday (same as the old behaviour)
            const day = now.getDay()
            const diffFromMonday = (day + 6) % 7
            const monday = new Date(now)
            monday.setDate(now.getDate() - diffFromMonday)
            monday.setHours(0, 0, 0, 0)
            return monday
        }
    }
}

function useResolvedTheme(theme: GraphWidgetOptions['theme']): 'dark' | 'light' {
    const [resolved, setResolved] = useState<'dark' | 'light'>(() => {
        if (theme === 'dark' || theme === 'light') return theme
        const prefersDark =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
        return prefersDark ? 'dark' : 'light'
    })

    useEffect(() => {
        if (theme === 'dark' || theme === 'light') {
            setResolved(theme)
            return
        }

        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            setResolved('light')
            return
        }

        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const update = () => setResolved(mql.matches ? 'dark' : 'light')
        update()

        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }

        // Safari
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [theme])

    return resolved
}

/** Viewport width below which Y-axis tick labels are hidden to save space on mobile. */
const MOBILE_BREAKPOINT = 480

function useIsMobile(breakpoint = MOBILE_BREAKPOINT): boolean {
    const [isMobile, setIsMobile] = useState(() => {
        return typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    })

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
        const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
        const update = () => setIsMobile(mql.matches)
        update()
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [breakpoint])

    return isMobile
}

export function GraphWidgetView(props: GraphWidgetViewProps) {
    const { graphId, title = 'Graph', height: heightProp, theme = 'auto', period, state } = props

    if (graphId === 'correlation') {
        return <CorrelationMatrixView title={title} theme={theme} period={period} state={state} />
    }

    if (graphId === 'rates_summary' || graphId === 'summary' || graphId === 'rates') {
        return <RatesSummaryView title={title} theme={theme} state={state} />
    }

    const isStrengthGraphId = graphId === 'strength' || graphId === 'strengths' || graphId === 'all'
    const hasJPY = /jpy/i.test(graphId)
    const METAL_PRICE_DIGITS: Record<string, number> = { xauusd: 2, xaujpy: 0, xagusd: 3 }
    const priceFractionDigits = METAL_PRICE_DIGITS[graphId.toLowerCase()] ?? (hasJPY ? 2 : 4)
    const isMobile = useIsMobile()
    const height =
        typeof heightProp === 'number'
            ? heightProp
            : isMobile
                ? (isStrengthGraphId ? 200 : 160)
                : (isStrengthGraphId ? 260 : 220)

    const resolveLegendFlagSrc = useMemo(() => {
        return (key: string) => {
            if (!key) return null
            const trimmed = key.trim()
            if (!/^[A-Za-z]{2,3}$/.test(trimmed)) return null
            return `/wp-content/uploads/2026/02/${trimmed.toLowerCase()}.png`
        }
    }, [])

    const resolvedTheme = useResolvedTheme(theme)

    const seriesData = useMemo(() => {
        if (state.status !== 'success') return null
        if (!state.data) return { kind: 'pair', points: [] } as GraphSeries
        return normalizeResponse(state.data, graphId) ?? ({ kind: 'pair', points: [] } as GraphSeries)
    }, [state, graphId])

    const isStrength = seriesData?.kind === 'strength'

    const strengthKeyOrder = useMemo(() => {
        if (!seriesData || seriesData.kind !== 'strength') return null
        const baseKeys = seriesData.keys
        if (seriesData.rows.length === 0) return baseKeys
        const lastRow = seriesData.rows[seriesData.rows.length - 1]
        return baseKeys
            .map((key, index) => {
                const raw = lastRow[key]
                const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number.NEGATIVE_INFINITY
                return { key, index, value }
            })
            .sort((a, b) => b.value - a.value || a.index - b.index)
            .map(({ key }) => key)
    }, [seriesData])

    const latestPoint = useMemo(() => {
        if (!seriesData || seriesData.kind !== 'pair' || seriesData.points.length === 0) return null
        return seriesData.points[seriesData.points.length - 1]
    }, [seriesData])

    const summary = useMemo(() => {
        if (!seriesData || seriesData.kind !== 'pair' || seriesData.points.length < 2) return null
        const first = seriesData.points[0]
        const last = seriesData.points[seriesData.points.length - 1]
        const delta = last.y - first.y
        const pct = first.y !== 0 ? (delta / first.y) * 100 : Number.NaN
        return { first, last, delta, pct }
    }, [seriesData])

    /** The first point's y value — used to convert normalised chart values back to actual prices. */
    const pairBaseY = useMemo(() => {
        if (!seriesData || seriesData.kind !== 'pair' || seriesData.points.length === 0) return null
        const y = seriesData.points[0].y
        return Number.isFinite(y) ? y : null
    }, [seriesData])

    const normalizedPairPoints = useMemo(() => {
        if (!seriesData || seriesData.kind !== 'pair' || seriesData.points.length === 0) return undefined
        if (pairBaseY === null) return seriesData.points
        return seriesData.points.map((point) => ({ ...point, y: point.y - pairBaseY }))
    }, [seriesData, pairBaseY])

    const chartData = useMemo(() => {
        if (!seriesData) return null
        const base = seriesData.kind === 'pair' ? normalizedPairPoints ?? seriesData.points : seriesData.rows
        if (!base || base.length === 0) return null
        if (seriesData.kind === 'pair') {
            return base.map((point, index) => {
                return {
                    ...point,
                    __index: index,
                }
            })
        }
        return base.map((point, index) => ({ ...point, __index: index }))
    }, [seriesData, normalizedPairPoints])

    const xAxisLabels = useMemo(() => {
        const rows = chartData as Record<string, unknown>[] | null
        const startFromData = rows && rows.length > 0 ? parseDateFromT(rows, 0) : null
        const endFromData = rows && rows.length > 1 ? parseDateFromT(rows, rows.length - 1) : null

        const startDate = startFromData ?? getPeriodFallbackStartDate(period)
        const startLabel = formatDateForPeriod(startDate, period)

        const endLabel = endFromData ? formatDateForPeriod(endFromData, period) : null

        return { startLabel, endLabel }
    }, [chartData, period])

    const currentPairBounds = useMemo(() => {
        if (!normalizedPairPoints || normalizedPairPoints.length === 0) return null
        let min = Number.POSITIVE_INFINITY
        let max = Number.NEGATIVE_INFINITY
        for (const point of normalizedPairPoints) {
            if (!Number.isFinite(point.y)) continue
            if (point.y < min) min = point.y
            if (point.y > max) max = point.y
        }
        if (!Number.isFinite(min) || !Number.isFinite(max)) return null
        // Always include 0 so the reference line and area fills remain visible
        return { min: Math.min(min, 0), max: Math.max(max, 0) }
    }, [normalizedPairPoints])

    const [pairBounds, setPairBounds] = useState<{ min: number; max: number } | null>(null)

    useEffect(() => {
        if (currentPairBounds === null) return
        setPairBounds((prev) => {
            if (prev === null) return currentPairBounds
            return {
                min: Math.min(prev.min, currentPairBounds.min),
                max: Math.max(prev.max, currentPairBounds.max),
            }
        })
    }, [currentPairBounds])

    useEffect(() => {
        setPairBounds(null)
    }, [graphId, period])

    const pairDomain = useMemo(() => {
        const bounds = pairBounds ?? currentPairBounds
        if (!bounds) return undefined
        const range = bounds.max - bounds.min
        const padding = range * 0.1 || 0.01
        return [bounds.min - padding, bounds.max + padding] as [number, number]
    }, [pairBounds, currentPairBounds])

    /** The latest normalised Y value (for the price reference line). */
    const latestNormalizedY = useMemo(() => {
        if (!normalizedPairPoints || normalizedPairPoints.length === 0) return null
        const last = normalizedPairPoints[normalizedPairPoints.length - 1]
        return Number.isFinite(last.y) ? last.y : null
    }, [normalizedPairPoints])

    const xGridLines = useMemo(() => {
        if (!chartData || chartData.length === 0) return []
        const lines: number[] = []
        for (let i = 24; i < chartData.length; i += 24) {
            lines.push(i)
        }
        return lines
    }, [chartData])

    const xDomain = useMemo(() => {
        if (!chartData || chartData.length === 0) return undefined
        return [0, chartData.length - 1] as [number, number]
    }, [chartData])

    const palette = useMemo(() => {
        const isDark = resolvedTheme === 'dark'
        const up = '#22c55e'
        const down = '#ef4444'
        const accent = summary && summary.delta < 0 ? down : up

        return {
            isDark,
            accent,
            bg: isDark ? '#0b1220' : '#ffffff',
            panel: isDark ? '#0f172a' : '#ffffff',
            border: isDark ? 'rgba(148,163,184,0.18)' : '#e5e7eb',
            text: isDark ? '#e5e7eb' : '#111827',
            muted: isDark ? 'rgba(226,232,240,0.65)' : '#6b7280',
            grid: isDark ? 'rgba(148,163,184,0.10)' : 'rgba(107,114,128,0.18)',
            axis: isDark ? 'rgba(226,232,240,0.55)' : 'rgba(17,24,39,0.55)',
            tooltipBg: isDark ? 'rgba(2,6,23,0.92)' : 'rgba(255,255,255,0.96)',
            tooltipBorder: isDark ? 'rgba(148,163,184,0.22)' : 'rgba(17,24,39,0.14)',
        }
    }, [resolvedTheme, summary])

    const strengthColors = useMemo(() => {
        return resolvedTheme === 'dark'
            ? ['#38bdf8', '#f59e0b', '#22c55e', '#ef4444', '#eab308', '#14b8a6']
            : ['#0284c7', '#d97706', '#16a34a', '#dc2626', '#ca8a04', '#0f766e']
    }, [resolvedTheme])

    const containerStyle: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        fontFamily:
            'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: 12,
        background: palette.panel,
        color: palette.text,
    }

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'nowrap',
        marginBottom: 8,
    }

    const titleStyle: React.CSSProperties = {
        fontSize: 'clamp(11px, 2.8vw, 14px)',
        fontWeight: 600,
        margin: 0,
        flex: '1 1 auto',
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }

    const metaStyle: React.CSSProperties = {
        fontSize: 12,
        color: palette.muted,
        margin: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '60%',
    }

    const badgeStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 'clamp(2px, 0.8vw, 6px)',
        fontSize: 11,
        padding: 0,
        color: palette.muted,
        flexShrink: 0,
    }

    const latestValueStyle: React.CSSProperties = {
        fontSize: 'clamp(11px, 2.2vw, 20px)',
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: palette.text,
        fontVariantNumeric: 'tabular-nums',
    }

    const changeStyle: React.CSSProperties = {
        fontSize: 'clamp(9px, 1.6vw, 11px)',
        fontWeight: 600,
        color: summary && summary.delta < 0 ? '#ef4444' : '#22c55e',
    }

    const chartWrapperStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        height: '100%',
    }

    const xAxisLabelBaseStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: 0,
        fontSize: 12,
        lineHeight: 1,
        color: palette.muted,
    }

    const xAxisStartLabelStyle: React.CSSProperties = {
        ...xAxisLabelBaseStyle,
        left: 0,
    }

    const chartRightMargin = !isStrength && latestPoint ? 38 : 12

    const xAxisEndLabelStyle: React.CSSProperties = {
        ...xAxisLabelBaseStyle,
        right: chartRightMargin,
    }

    const strengthLegendKeys =
        isStrength && seriesData?.kind === 'strength' ? strengthKeyOrder ?? seriesData.keys : null

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <p style={titleStyle}>{title}</p>
                {latestPoint ? (
                    <div style={badgeStyle}>
                        <span style={latestValueStyle}>{formatNumber(latestPoint.y, priceFractionDigits)}</span>
                        {summary ? <span style={changeStyle}>{formatPercent(summary.pct)}</span> : null}
                    </div>
                ) : (
                    <p style={metaStyle}>{state.status === 'success' ? '' : 'Loading...'}</p>
                )}
            </div>

            {state.status === 'loading' || state.status === 'idle' ? (
                <div style={{ height, display: 'grid', placeItems: 'center', color: '#6b7280' }}>Loading...</div>
            ) : state.status === 'error' ? (
                <div style={{ height, display: 'grid', placeItems: 'center', color: '#b91c1c' }}>
                    Failed to load: {state.message}
                </div>
            ) : (
                <div
                    style={
                        strengthLegendKeys
                            ? {
                                width: '100%',
                                height,
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: isMobile ? 4 : 12,
                            }
                            : { width: '100%', height }
                    }
                >
                    <div
                        style={
                            strengthLegendKeys
                                ? isMobile
                                    ? { flex: 1, minWidth: 0, minHeight: 0 }
                                    : { flex: 1, minWidth: 0 }
                                : { width: '100%', height: '100%' }
                        }
                    >
                        <div style={chartWrapperStyle}>
                            <ResponsiveContainer>
                                <ComposedChart data={chartData ?? undefined} margin={{ top: 8, right: chartRightMargin, bottom: isStrength ? 20 : 4, left: 0 }}>
                                    <CartesianGrid stroke={palette.grid} strokeWidth={0.5} horizontal={!isStrength} vertical={false} />
                                    {xGridLines.map((x) => (
                                        <ReferenceLine key={`grid-${x}`} x={x} stroke={palette.grid} strokeWidth={0.5} />
                                    ))}
                                    <ReferenceLine y={0} stroke={palette.axis} strokeDasharray="4 4" />
                                    {!isStrength && latestNormalizedY !== null && latestPoint ? (
                                        <ReferenceLine
                                            y={latestNormalizedY}
                                            stroke={palette.accent}
                                            strokeDasharray="3 3"
                                            strokeWidth={1}
                                            label={{
                                                value: formatNumber(latestPoint.y, priceFractionDigits),
                                                position: 'right',
                                                fill: palette.accent,
                                                fontSize: 9,
                                                fontWeight: 700,
                                            }}
                                        />
                                    ) : null}
                                    <XAxis
                                        dataKey="__index"
                                        type="number"
                                        domain={xDomain}
                                        hide={isStrength}
                                        tick={false}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        tick={isStrength || isMobile ? false : { fill: palette.muted, fontSize: 10 }}
                                        tickLine={false}
                                        axisLine={false}
                                        width={isStrength || isMobile ? 8 : 44}
                                        domain={isStrength ? ['auto', 'auto'] : pairDomain}
                                        tickFormatter={(value) => {
                                            const v = Number(value)
                                            return formatNumber(pairBaseY !== null ? v + pairBaseY : v, priceFractionDigits)
                                        }}
                                    />
                                    {isStrength ? null : (
                                        <Line
                                            type="linear"
                                            dataKey="y"
                                            stroke={palette.accent}
                                            strokeWidth={1.5}
                                            dot={false}
                                            isAnimationActive={false}
                                        />
                                    )}
                                    {isStrength && seriesData?.kind === 'strength'
                                        ? (strengthKeyOrder ?? seriesData.keys).map((key, index) => (
                                            <Line
                                                key={key}
                                                type="linear"
                                                dataKey={key}
                                                stroke={strengthColors[index % strengthColors.length]}
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{ r: 3, strokeWidth: 0 }}
                                                isAnimationActive={false}
                                            />
                                        ))
                                        : null}
                                </ComposedChart>
                            </ResponsiveContainer>
                            <div style={xAxisStartLabelStyle}>{xAxisLabels.startLabel}</div>
                            {xAxisLabels.endLabel ? (
                                <div style={xAxisEndLabelStyle}>{xAxisLabels.endLabel}</div>
                            ) : null}
                        </div>
                    </div>
                    {strengthLegendKeys ? (
                        <div
                            style={
                                isMobile
                                    ? {
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        rowGap: 4,
                                        columnGap: 10,
                                        fontSize: 11,
                                        color: palette.muted,
                                    }
                                    : {
                                        width: 80,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        gap: 6,
                                        fontSize: 12,
                                        color: palette.muted,
                                    }
                            }
                        >
                            {strengthLegendKeys.map((key, index) => (
                                <div
                                    key={key}
                                    style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8, whiteSpace: 'nowrap' }}
                                >
                                    <span
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 2,
                                            background: strengthColors[index % strengthColors.length],
                                            display: 'inline-block',
                                        }}
                                    />
                                    {(() => {
                                        const flagSrc = resolveLegendFlagSrc(key)
                                        if (!flagSrc) return null
                                        return (
                                            <img
                                                src={flagSrc}
                                                alt=""
                                                width={14}
                                                height={10}
                                                loading="lazy"
                                                decoding="async"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none'
                                                }}
                                                style={{
                                                    width: 14,
                                                    height: 10,
                                                    borderRadius: 2,
                                                    border: `1px solid ${palette.border}`,
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        )
                                    })()}
                                    <span>{key}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    )
}
